import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download, FileText, CheckCircle2, Sparkles, Zap, Lock, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { aiAPI, prototypeAPI, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const PROTOTYPE_FULL_COST = 150;

function blobToObjectURL(blob) {
  return window.URL.createObjectURL(blob);
}

export default function ProposalViewPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadHref, setDownloadHref] = useState(null);
  const [prototypes, setPrototypes] = useState([]); // [{id, is_teaser, status, ...}]
  const [teaserBusy, setTeaserBusy] = useState(false);
  const [fullBusy, setFullBusy] = useState(false);

  const teaser = prototypes.find(p => p.is_teaser);
  const fullProto = prototypes.find(p => !p.is_teaser);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [leadRes, protoRes] = await Promise.all([
          aiAPI.getLead(leadId),
          prototypeAPI.listForLead(leadId).catch(() => ({ data: [] })),
        ]);
        if (mounted) {
          setLead(leadRes.data);
          setPrototypes(protoRes.data || []);
        }
      } catch (err) {
        if (mounted) toast.error("We couldn't find that proposal. It may have expired.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [leadId]);

  // Note: when redirected back with ?unlock=1, the user clicks the Unlock Full button themselves.
  // This is cleaner UX than auto-triggering.

  const handleGenerateTeaser = async () => {
    setTeaserBusy(true);
    try {
      const res = await prototypeAPI.generateTeaser(leadId);
      navigate(`/prototype-preview/${res.data.id}`);
    } catch (err) {
      toast.error("Couldn't start prototype generation. Please retry.");
      setTeaserBusy(false);
    }
  };

  const handleUnlockFull = async () => {
    if (!user) {
      navigate(`/login?next=/proposal/${leadId}?unlock=1`);
      return;
    }
    setFullBusy(true);
    try {
      const res = await prototypeAPI.generateFull(leadId);
      if (refreshUser) refreshUser();
      navigate(`/prototype-preview/${res.data.id}`);
    } catch (err) {
      const detail = apiErrorMessage(err, "Couldn't generate the full prototype. Please retry.");
      toast.error(detail);
      setFullBusy(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await aiAPI.downloadProposalPDF(leadId);
      const url = blobToObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setDownloadHref(url);
      toast.success('Proposal ready — click below to save');
    } catch (err) {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="animate-spin w-8 h-8 border-2 rounded-full" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Proposal not found</h1>
          <p className="mb-8" style={{ color: 'var(--text-muted)' }}>
            We couldn&apos;t locate this proposal. Want to generate a new one?
          </p>
          <Link to="/start-project">
            <Button style={{ background: 'var(--primary)', color: 'var(--background)' }}>
              Request for Proposal <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const evaluation = lead.ai_evaluation || {};
  const budget = evaluation.estimated_budget_usd || {};

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      <SEO
        path={`/proposal/${leadId}`}
        title={`Proposal for ${lead.name || 'your project'}`}
        description={`AI-generated proposal: ${(lead.description || '').slice(0, 140)}`}
      />
      <Navbar />

      <section className="max-w-4xl mx-auto px-6 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
            style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}
          >
            <Sparkles className="w-3.5 h-3.5" /> AI-Generated Proposal
          </div>

          <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-3" data-testid="proposal-heading">
            Proposal for {lead.name || 'your project'}
          </h1>
          <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
            {lead.email ? `Sent to ${lead.email}` : 'Free preview — no signup required'}
          </p>

          {/* Quick stats */}
          {(evaluation.recommended_stack || evaluation.estimated_timeline_weeks) && (
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {evaluation.estimated_timeline_weeks && (
                <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Timeline</div>
                  <div className="text-xl font-semibold">{evaluation.estimated_timeline_weeks} weeks</div>
                </div>
              )}
              {(budget.min || budget.max) && (
                <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Investment</div>
                  <div className="text-xl font-semibold">
                    ${(budget.min || 0).toLocaleString()} – ${(budget.max || 0).toLocaleString()}
                  </div>
                </div>
              )}
              {evaluation.complexity && (
                <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                  <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Complexity</div>
                  <div className="text-xl font-semibold capitalize">{evaluation.complexity}</div>
                </div>
              )}
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 mb-10">
            {downloadHref ? (
              <a href={downloadHref} download={`XceedAgents-Proposal-${lead.name || 'Project'}.pdf`}>
                <Button className="gap-2" style={{ background: '#10B981', color: '#fff' }} data-testid="proposal-download-ready">
                  <Download className="w-4 h-4" /> Click to save PDF
                </Button>
              </a>
            ) : (
              <Button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="gap-2"
                style={{ background: 'var(--primary)', color: 'var(--background)' }}
                data-testid="proposal-download-pdf"
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Preparing…' : 'Prepare PDF'}
              </Button>
            )}
            <Link to="/start-project">
              <Button variant="outline" className="gap-2" data-testid="proposal-new-project">
                <FileText className="w-4 h-4" /> New Project
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="gap-2" data-testid="proposal-workspace">
                Workspace <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* ============ PROTOTYPE CTA BLOCK ============ */}
          <div
            className="mb-10 p-6 lg:p-8 rounded-2xl border-2"
            style={{ borderColor: 'var(--primary-accent)', background: 'rgba(234, 88, 12, 0.04)' }}
            data-testid="prototype-cta-block"
          >
            <div className="flex flex-wrap items-start gap-2 mb-3">
              <Zap className="w-5 h-5 mt-0.5" style={{ color: 'var(--primary-accent)' }} />
              <h2 className="font-heading text-2xl lg:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                See your project come to life
              </h2>
            </div>
            <p className="text-base mb-5" style={{ color: 'var(--text-secondary)' }}>
              Our AI can generate a working interactive prototype of this project — clickable pages, working forms, realistic data — right now. Start with a free single-page teaser. Unlock the full multi-page prototype with your free credits.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {/* TEASER CARD */}
              <div className="p-5 rounded-xl border bg-white" style={{ borderColor: 'var(--border)' }} data-testid="proto-teaser-card">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase mb-2" style={{ color: 'var(--primary-accent)' }}>
                  <Eye className="w-3.5 h-3.5" /> Free Teaser
                </div>
                <h3 className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Single-page preview</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  A landing page mock-up of your project — instant, no signup, no credits.
                </p>
                {teaser?.status === 'ready' ? (
                  <Link to={`/prototype-preview/${teaser.id}`}>
                    <Button className="w-full gap-2" style={{ background: 'var(--text-primary)', color: '#fff' }} data-testid="proto-view-teaser">
                      <Eye className="w-4 h-4" /> View Teaser
                    </Button>
                  </Link>
                ) : teaser?.status === 'generating' ? (
                  <Link to={`/prototype-preview/${teaser.id}`}>
                    <Button className="w-full gap-2" disabled style={{ background: 'var(--text-muted)', color: '#fff' }}>
                      Generating… (click to view progress)
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={handleGenerateTeaser}
                    disabled={teaserBusy}
                    className="w-full gap-2"
                    style={{ background: 'var(--text-primary)', color: '#fff' }}
                    data-testid="proto-generate-teaser"
                  >
                    {teaserBusy ? 'Starting…' : (<><Sparkles className="w-4 h-4" /> Generate Free Teaser</>)}
                  </Button>
                )}
              </div>

              {/* FULL CARD */}
              <div className="p-5 rounded-xl border-2 bg-white relative" style={{ borderColor: 'var(--primary-accent)' }} data-testid="proto-full-card">
                <div className="absolute -top-2.5 right-4 px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase rounded" style={{ background: 'var(--primary-accent)', color: '#fff' }}>
                  Recommended
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase mb-2" style={{ color: 'var(--primary-accent)' }}>
                  <Zap className="w-3.5 h-3.5" /> Full Prototype · {PROTOTYPE_FULL_COST} credits
                </div>
                <h3 className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Multi-page working app</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  5-7 working pages with navigation, forms, dashboards, and realistic data — your actual product, prototyped.
                </p>
                {fullProto?.status === 'ready' ? (
                  <Link to={`/prototype-preview/${fullProto.id}`}>
                    <Button className="w-full gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="proto-view-full">
                      <Eye className="w-4 h-4" /> View Full Prototype
                    </Button>
                  </Link>
                ) : fullProto?.status === 'generating' ? (
                  <Link to={`/prototype-preview/${fullProto.id}`}>
                    <Button className="w-full gap-2" disabled style={{ background: 'var(--text-muted)', color: '#fff' }}>
                      Building… (click to view progress)
                    </Button>
                  </Link>
                ) : !user ? (
                  <Link to={`/login?next=/proposal/${leadId}?unlock=1`}>
                    <Button className="w-full gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="proto-signup-to-unlock">
                      <Lock className="w-4 h-4" /> Sign in to Unlock (100 free credits)
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={handleUnlockFull}
                    disabled={fullBusy || (user.credits ?? 0) < PROTOTYPE_FULL_COST}
                    className="w-full gap-2"
                    style={{ background: 'var(--primary-accent)', color: '#fff' }}
                    data-testid="proto-unlock-full"
                  >
                    {fullBusy ? 'Starting…' : (user.credits ?? 0) < PROTOTYPE_FULL_COST
                      ? `Need ${PROTOTYPE_FULL_COST - (user.credits ?? 0)} more credits`
                      : (<><Zap className="w-4 h-4" /> Spend {PROTOTYPE_FULL_COST} credits & generate</>)}
                  </Button>
                )}
                {user && (
                  <p className="text-xs mt-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    You have <strong style={{ color: 'var(--primary-accent)' }}>{user.credits ?? 0}</strong> credits available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Proposal body */}
          {lead.ai_proposal ? (
            <div
              className="prose max-w-none rounded-2xl border p-6 lg:p-10"
              style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--text-primary)' }}
              data-testid="proposal-body"
            >
              <pre className="whitespace-pre-wrap font-body text-[15px] leading-relaxed" style={{ fontFamily: 'inherit' }}>
                {lead.ai_proposal}
              </pre>
            </div>
          ) : (
            <div className="p-6 rounded-xl border text-sm" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              The proposal is still being generated. Refresh in a moment.
            </div>
          )}

          {/* Key features */}
          {evaluation.key_features?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading text-2xl font-semibold mb-4">What we&apos;ll build</h2>
              <ul className="space-y-2">
                {evaluation.key_features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Next steps */}
          <div className="mt-12 p-6 lg:p-8 rounded-2xl border" style={{ borderColor: 'var(--border)', background: 'var(--tag-bg)' }}>
            <h3 className="font-heading text-xl font-semibold mb-2">Ready to move forward?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              Create a free workspace to chat with our team, get a working prototype, and track delivery in real time. New accounts get <strong>100 free credits</strong>.
            </p>
            <Link to="/login">
              <Button style={{ background: 'var(--primary)', color: 'var(--background)' }} data-testid="proposal-cta-workspace">
                Open My Workspace <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
